import { type FC, useCallback, useMemo } from 'react'

import { FanResponseDto } from '@api'
import { useUnit } from 'effector-react'

import { $originName } from '@/dialogs/EditFanOriginDialog/models.ts'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import {
  $fanBirthday,
  $fanCountryCode,
} from '@/models/chattingUI/chats/chats.ts'
import {
  $isTimezoneSync,
  syncTimeZoneWithCountryFx,
} from '@/models/chattingUI/fanInfo/timezones.ts'
import { $creatorId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'
import EditIcon from '@/modules/chatting/icons/edit.svg'
import { ListItem } from '@/modules/chatting/ui/chat-window/fan-info/ListItem.tsx'
import { LocalTime } from '@/modules/chatting/ui/chat-window/fan-info/LocalTime.tsx'
import { IconButton } from '@/modules/chatting/ui/components/icon-button.tsx'
import {
  TrackedFeatures,
  trackFeatureUsage,
} from '@/utils/feature-engagement.ts'
import { classNames } from '@/utils/helpers'
import { getSubcriptionState } from '@/utils/subscription.ts'

import MapIcon from '../../../icons/map.svg'
import { hasRebillOn, toDateFormat } from '../../utils'
import { EditCountry } from './actions/EditCountry/EditCountry.tsx'
import { EditBirthday } from './actions/edit-birthday'
import { getSubscriptionRows } from './getSubscriptionRows.tsx'
import { getRelativeDate, toUsdFormat } from './utils'

export interface Props {
  details: FanResponseDto
}

export const FanDetailsCard: FC<Props> = ({ details }) => {
  const countryCode = useUnit($fanCountryCode)
  const birthday = useUnit($fanBirthday)
  const originName = useUnit($originName)
  const creatorId = useUnit($creatorId)

  const { displayName, username, name, lastSeen, subscribedOnData } = details
  const isTimezoneSync = useUnit($isTimezoneSync)
  const isSyncInProgress = useUnit(syncTimeZoneWithCountryFx.pending)
  const { isSubscribed } = getSubcriptionState(subscribedOnData)

  const handleClickEditOrigin = () => {
    openDialog({
      type: DIALOG_TYPE.EDIT_FAN_ORIGIN,
    })
  }

  const spent = subscribedOnData
    ? Math.trunc(
        (subscribedOnData.totalSumm || 0) -
          (subscribedOnData.subscribesSumm || 0),
      )
    : 0
  const tips = subscribedOnData?.tipsSumm ?? 0

  const spentCards = [
    {
      title: 'Total Spend',
      value: toUsdFormat(spent),
    },
    {
      title: 'Tips',
      value: toUsdFormat(tips),
    },
  ]

  const subscriptionRows = useMemo(
    () =>
      getSubscriptionRows({
        subscribed: isSubscribed,
        duration: subscribedOnData?.duration,
        renewOn: hasRebillOn(details),
        nextPayment: details?.subscribedOnData?.renewedAt,
      }),
    [isSubscribed, subscribedOnData?.duration, details],
  )

  const birthdayString =
    birthday && birthday !== 'Undefined' ? toDateFormat(birthday) : 'Not set'

  const handleFanDetailsClick = useCallback(() => {
    trackFeatureUsage(TrackedFeatures.FanInfo, creatorId)
  }, [creatorId])

  return (
    <div
      className="bg-card rounded-md p-3"
      onClick={handleFanDetailsClick}
    >
      <div className="space-y-6">
        <div className="space-y-1.5">
          <p className="text-[26px] font-semibold leading-[31px]">
            {displayName || name || username}
          </p>
          {lastSeen && (
            <p className="text-secondary-300 text-sm font-medium">
              Last seen {getRelativeDate(lastSeen)}
            </p>
          )}
          <p
            className={classNames(
              'text-sm font-medium',
              isSubscribed ? 'text-success-default' : 'text-warning-dark',
            )}
          >
            {isSubscribed ? 'Subscribed' : 'Not Subscribed'}
          </p>
        </div>
        <div className="flex gap-3">
          {spentCards.map(({ title, value }) => (
            <div
              key={title}
              className="bg-secondary-50 dark:bg-secondary-600 flex-1 space-y-1.5 rounded-md p-2 text-center"
            >
              <p className="text-[26px] font-semibold leading-[31px]">
                {value}
              </p>
              <p className="text-secondary-300 text-sm font-medium leading-[17px]">
                {title}
              </p>
            </div>
          ))}
        </div>
        <div className="space-y-3">
          {subscriptionRows.map(({ label, value }) => (
            <ListItem
              label={label}
              value={<span className="text-title">{value}</span>}
              key={label}
            />
          ))}
          {subscribedOnData && (
            <>
              <ListItem
                label="Birthday"
                value={
                  <>
                    {birthdayString.includes('NaN')
                      ? 'Not set'
                      : birthdayString}
                    <EditBirthday />
                  </>
                }
              />
              <LocalTime />
              {/* <ListItem
                label="PayDay"
                value={
                  <>
                    <span>Not set</span>
                    <EditPayDay userId={id} payday={null} />
                  </>
                }
              /> */}
              <ListItem
                label="Fan Origin"
                value={
                  <>
                    <span>{originName ?? 'Not set'}</span>
                    <IconButton
                      icon={EditIcon}
                      onClick={handleClickEditOrigin}
                      className="text-primary-500"
                    />
                  </>
                }
              />
              <ListItem
                label={
                  <>
                    <span>Country</span>
                    <IconButton
                      icon={MapIcon}
                      iconClassName={
                        countryCode ? 'text-primary-500' : undefined
                      }
                      tooltip={
                        isTimezoneSync
                          ? 'The timezone is already synchronized'
                          : "Also set timezone to country's timezone"
                      }
                      disabled={!countryCode || isSyncInProgress}
                      onClick={syncTimeZoneWithCountryFx}
                    />
                  </>
                }
                value={<EditCountry />}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
